import { apiFetch } from "../../api/client";
import type { PartidoQueNoCierra } from "./noCoinciden";

/**
 * Las pocas llamadas donde el cliente compartido declara MENOS de lo que el
 * backend acepta. Viven acá y no en `api/organizer.ts` para no editar archivos
 * de la otra zona (ver AGENTS.md); el endpoint es el mismo.
 */

/**
 * Resolución administrativa del partido.
 *
 * `noShow` es la diferencia con el wrapper compartido, y no es cosmética: el
 * backend, sin ese campo, asume `"both"` — o sea "no vino nadie", 0 a 0 y
 * ningún equipo se lleva los puntos. Quién faltó no se puede deducir de ningún
 * dato, así que si no se manda, un W.O. cargado a favor de alguien queda
 * cargado a favor de nadie (ver WALKOVER_GOALS en organizer.service.ts).
 */
export async function setMatchAdministration(
  matchId: string,
  body: {
    status: "suspended" | "postponed" | "walkover" | "cancelled" | "scheduled";
    noShow?: "home" | "away" | "both" | null;
    notes?: string | null;
    publicNote?: string | null;
  },
): Promise<{ id: string; status: string }> {
  return apiFetch(`/api/v1/organizer/matches/${matchId}/administration`, {
    method: "PUT",
    body,
  });
}

/**
 * Baldes de partidos a nivel LIGA (`pending` / `upcoming` / `played`), el
 * mismo endpoint que come la app. `api/organizer.ts` sólo declara el fixture
 * por torneo (`/tournaments/{id}/matches`), que no trae los baldes ya
 * clasificados con el criterio de "esperando resultado" del backend.
 *
 * El balde `pending` mezcla dos cosas distintas para el organizador y por eso
 * se cuentan aparte en Actividad:
 *  - `pending`  = el árbitro cerró el acta y falta confirmarla (frena la tabla),
 *  - `scheduled`/`confirmed` = ya pasó la hora (+3 h) y nadie cargó nada.
 */
export type OrganizerMatchBucketRow = {
  id: string;
  status: string;
  matchdayLabel: string | null;
  tournamentId: string | null;
};

export type OrganizerMatchBuckets = {
  pending: OrganizerMatchBucketRow[];
  upcoming: OrganizerMatchBucketRow[];
  played: OrganizerMatchBucketRow[];
};

export async function fetchOrganizerMatchBuckets(): Promise<OrganizerMatchBuckets> {
  return apiFetch(`/api/v1/organizer/matches`);
}

/**
 * Novedades del usuario (Actividad → Novedades). Es el MISMO feed que lee la
 * app; no tiene nada que ver con las novedades que el organizador PUBLICA para
 * sus jugadores (`fetchLeagueNews`), que era lo que esta pestaña mostraba antes.
 *
 * `createdAt` es un instante real (SYSUTCDATETIME del server), no hora de
 * pared como `scheduled_at`: se muestra en la hora local del dispositivo.
 */
export type NotificationItem = {
  id: string;
  typeCode: string;
  title: string;
  body: string | null;
  leagueId: string | null;
  readAt: string | null;
  createdAt: string | null;
};

export async function fetchMyNotifications(): Promise<{ notifications: NotificationItem[] }> {
  return apiFetch(`/api/v1/me/notifications`);
}

/**
 * Organizadores de la liga: listar, invitar por email, revocar. La invitación
 * es NOMINADA (sólo la acepta una cuenta con ese email), de un solo uso y
 * vence a los 14 días. Endpoints nuevos de league-organizers.routes.ts.
 */
export type LeagueOrganizer = {
  userId: string;
  displayName: string;
  email: string;
  since: string | null;
  isCreator: boolean;
};

export type OrganizerInvitation = {
  id: string;
  email: string;
  createdAt: string | null;
  expiresAt: string | null;
  invitedByName: string | null;
  isExpired: boolean;
};

export async function fetchLeagueOrganizers(
  leagueId: string,
): Promise<{ organizers: LeagueOrganizer[]; invitations: OrganizerInvitation[] }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/organizers`);
}

export async function inviteLeagueOrganizer(
  leagueId: string,
  email: string,
): Promise<{ invitation: OrganizerInvitation; inviteUrl: string; emailSent: boolean }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/organizer-invitations`, {
    method: "POST",
    body: { email },
  });
}

export async function cancelOrganizerInvitation(
  leagueId: string,
  invitationId: string,
): Promise<{ cancelled: boolean }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/organizer-invitations/${invitationId}`, {
    method: "DELETE",
  });
}

export async function revokeLeagueOrganizer(
  leagueId: string,
  userId: string,
): Promise<{ removed: boolean; selfRemoved: boolean }> {
  return apiFetch(`/api/v1/organizer/leagues/${leagueId}/organizers/${userId}`, {
    method: "DELETE",
  });
}

/** Preview público de una invitación de organizador (la abre el invitado). */
export type OrganizerInvitePreview = {
  leagueId: string;
  leagueName: string;
  leagueLogoUrl: string | null;
  invitedByName: string | null;
  invitedEmailMasked: string;
  role: "organizer";
};

export async function previewOrganizerInvitation(token: string): Promise<OrganizerInvitePreview> {
  // withAuth: false — el visitante normalmente NO tiene sesión todavía.
  return apiFetch(`/api/v1/public/organizer-invitations/${encodeURIComponent(token)}`, {
    withAuth: false,
  });
}

export async function acceptOrganizerInvitation(
  token: string,
): Promise<{ status: "joined"; leagueId: string; leagueName: string }> {
  return apiFetch(`/api/v1/me/organizer-invitations/${encodeURIComponent(token)}/accept`, {
    method: "POST",
  });
}

/**
 * Invitaciones a organizar que esperan a ESTA cuenta (por su email). Las
 * muestra la bienvenida del que todavía no tiene liga, para que no dependa de
 * encontrar el mail. Se aceptan por id: el token sólo viaja en el link.
 */
export type MyOrganizerInvitation = {
  id: string;
  leagueId: string;
  leagueName: string;
  leagueLogoUrl: string | null;
  invitedByName: string | null;
  expiresAt: string | null;
};

export async function fetchMyOrganizerInvitations(): Promise<{ invitations: MyOrganizerInvitation[] }> {
  return apiFetch(`/api/v1/me/pending-organizer-invitations`);
}

export async function acceptOrganizerInvitationById(
  invitationId: string,
): Promise<{ status: "joined"; leagueId: string; leagueName: string }> {
  return apiFetch(`/api/v1/me/pending-organizer-invitations/${invitationId}/accept`, { method: "POST" });
}

/** Contacto PÚBLICO del capitán: sólo viene lo que el jugador habilitó en su
 *  perfil de comunidad (show_whatsapp / show_instagram). */
export type CaptainContact = {
  displayName: string;
  whatsapp: string | null;
  instagram: string | null;
};

/**
 * Lo que el detalle del partido muestra y el fixture del torneo NO trae: el
 * torneo, la nota interna, la última modificación y los capitanes. Es el mismo
 * endpoint que come el detalle de la app (`getOrganizerMatchDetail`); de acá
 * se usan sólo esos extras — el partido en sí sigue saliendo del fixture, que
 * trae el status crudo de la base (este lo devuelve mapeado: "pending").
 */
export type OrganizerMatchDetailExtras = {
  tournamentName: string | null;
  notes: string | null;
  publicNote: string | null;
  lastUpdateLabel: string | null;
  homeTeamCaptainContact: CaptainContact | null;
  awayTeamCaptainContact: CaptainContact | null;
};

export async function fetchOrganizerMatchDetail(matchId: string): Promise<OrganizerMatchDetailExtras> {
  return apiFetch(`/api/v1/organizer/matches/${matchId}`);
}

/** Marca todas las no leídas. Se llama DESPUÉS de pintar: los destacados de
 *  esta pasada salen del fetch, no del estado del server. */
export async function markMyNotificationsRead(): Promise<{ marked: number }> {
  return apiFetch(`/api/v1/me/notifications/read`, { method: "POST" });
}

/**
 * "Qué me falta cerrar", en TODAS las ligas que organizo.
 *
 * El ámbito lo decide el backend con los ROLES del usuario, no con el contexto
 * activo: por eso no lleva ningún parámetro. Reemplaza el conteo que salía de
 * `fetchOrganizerMatchBuckets`, que es la lista de UNA liga (la del contexto) y
 * con varias ligas dejaba afuera el trabajo de las demás.
 */
export type ResumenPendientes = {
  /** `noCoinciden` es P3 y llega opcional: un backend viejo no lo manda. */
  total: { actasPorConfirmar: number; esperandoResultado: number; noCoinciden?: number };
  porLiga: Array<{
    leagueId: string;
    leagueName: string;
    actasPorConfirmar: number;
    esperandoResultado: number;
    /** P3 · goles cargados que no suman el resultado (ventana de 10 días). */
    noCoinciden?: number;
    /** Los de P3, el más reciente primero (tope 5). */
    partidosQueNoCoinciden?: PartidoQueNoCierra[];
    torneos: Array<{
      tournamentId: string;
      tournamentName: string;
      actasPorConfirmar: number;
      esperandoResultado: number;
      noCoinciden?: number;
    }>;
  }>;
};

export async function fetchPendingSummary(): Promise<ResumenPendientes> {
  return apiFetch(`/api/v1/organizer/pending/summary`);
}
